import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useI18n } from "../i18n";

/**
 * iter64 — CMS Content hook.
 * Fetches /api/site-content (per-language) and caches in memory + sessionStorage
 * for TTL_MS. Passing a section key returns just that node.
 * iter64 corte 5 — lang-aware: EN variant served with per-key fallback to ES.
 */

const TTL_MS = 60_000;
const CACHE_PREFIX = "avisocar_cms_v2_";
const inFlight = {}; // lang -> promise
const memory = {}; // lang -> { fetched_at, data }

function readSession(lang) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.fetched_at > TTL_MS) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

function writeSession(lang, payload) {
  try { sessionStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(payload)); } catch (_e) { /* quota */ }
}

async function fetchCMS(lang, force = false) {
  if (!force) {
    if (memory[lang] && Date.now() - memory[lang].fetched_at < TTL_MS) return memory[lang].data;
    const cached = readSession(lang);
    if (cached) {
      memory[lang] = cached;
      return cached.data;
    }
  }
  if (inFlight[lang]) return inFlight[lang];
  inFlight[lang] = axios.get(`/api/site-content?lang=${lang}`, { headers: { "Cache-Control": "no-cache" } })
    .then((r) => {
      const payload = { fetched_at: Date.now(), data: r.data };
      memory[lang] = payload;
      writeSession(lang, payload);
      inFlight[lang] = null;
      return r.data;
    })
    .catch((e) => {
      inFlight[lang] = null;
      throw e;
    });
  return inFlight[lang];
}

export function invalidateCMS() {
  ["es", "en"].forEach((lang) => {
    memory[lang] = null;
    try { sessionStorage.removeItem(CACHE_PREFIX + lang); } catch (_e) { /* ignore */ }
  });
}

export function useCMSContent(section = null) {
  const { cmsLang } = useI18n();
  const [data, setData] = useState(() => memory[cmsLang]?.data || null);
  const [loading, setLoading] = useState(!memory[cmsLang]);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    setError(null);
    try {
      const d = await fetchCMS(cmsLang, force);
      setData(d);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [cmsLang]);

  useEffect(() => { load(false); }, [load]);

  if (section && data) {
    return { data: data[section], full: data, loading, error, refresh: () => load(true) };
  }
  return { data, loading, error, refresh: () => load(true) };
}

export default useCMSContent;
